import { createSelector, createStructuredSelector } from "reselect"
import { formValueSelector, getFormValues } from "redux-form/immutable"
import { fromJS, Map as ImmutableMap } from "immutable"

// action creators
import { onRemoveItemHandler } from "app/static/frontend/dashboards/action-creators"
import { saveDashboard } from "app/static/frontend/dashboards/action-creators/dashboardHeaderActionCreators"

// selectors
import { selectHomepageDashboardId } from "shared/userdata/userdata-selectors"

// constants
import dashboardConstants from "app/static/frontend/dashboards/constants"
import { dashboardPaths } from "app/static/frontend/imports/desktopPaths"
import { QUORUM_BLUE, WIDGET_EDIT_FORM_KEY } from "shared/imports/sharedConstants"

// helpers
import { saveElementAsImage } from "app/static/frontend/imports/desktopHelperFunctions"

// Dashboard container selectors
// app/static/frontend/dashboards/containers/Dashboard

// select the dashboards object from the global redux store
// this is instantiated by the app/static/frontend/dashboards/reducer
// in app/static/frontend/reducer
export const selectDashboardsStore = (state) => state.dashboards

export const selectDashboardsPropParam = (param) =>
    createSelector(
        (state, props) => props.params && props.params[param],
        (prop) => prop,
    )

export const selectWidgetTemplates = createSelector(selectDashboardsStore, (store) =>
    store.getIn(["widgetTemplates", "templates"]),
)

export const selectWidgetTemplatesLoading = createSelector(selectDashboardsStore, (store) =>
    store.getIn(["widgetTemplates", "isLoading"]),
)

export const selectWidgetTemplatesHasMore = createSelector(selectDashboardsStore, (store) =>
    store.getIn(["widgetTemplates", "hasMore"]),
)

const createDashboardTemplateSelector = (key) =>
    createSelector(selectDashboardsStore, (store) => store.getIn(["dashboardTemplates", key]))
export const selectDashboardTemplates = createDashboardTemplateSelector("templates")
export const selectDashboardTemplatesLoading = createDashboardTemplateSelector("isLoading")
export const selectDashboardTemplatesHasMore = createDashboardTemplateSelector("hasMore")

export const createSelectTemplateObjsForCreateForm = (selectTemplateSelector) =>
    createSelector(
        selectTemplateSelector,
        (templates) =>
            templates &&
            templates.map((template) => ({
                title: template.get("name"),
                text: template.get("description"),
                icon: template.get("icon"),
                id: template.get("id"),
                widget: template.get("widget"),
                widgets: template.get("widgets"),
                positions: template.get("positions"),
            })),
    )

export const selectWidgetTemplateObjsForCreateForm = createSelectTemplateObjsForCreateForm(selectWidgetTemplates)
export const selectDashboardTemplateObjsForCreateForm = createSelectTemplateObjsForCreateForm(selectDashboardTemplates)

// trys to grab dashboard's id from props, if cant, grabs it from route params
export const selectDashboardId = createSelector(
    (_, props) => props.dashboardId,
    selectDashboardsPropParam("dashboardId"),
    (idFromProps, idFromParams) => idFromProps || idFromParams,
)

// selects the id of the current dashboard
// the id itself comes from react-router
// we define paths.dashboard in app/static/frontend/imports/desktopPaths
// this means that when we navigate to /dashboard/{$dashboardId}/,
//      react router will have the id of the dashboard saved to its params
export const selectDashboardsById = createSelector(selectDashboardsStore, selectDashboardId, (store, dashboardId) =>
    store.get(dashboardId),
)

// ideally we would use the render prop on Route to just pass this down,
// but unfortunately that is not available till react-router 4
export const selectIsExternal = (_, props) => window.location.pathname.includes("external")

// selects a value in a dashboard
export const selectDashboardsValue = (key) =>
    createSelector(selectDashboardsById, (store) =>
        Array.isArray(key) ? store && store.getIn(key) : store && store.get(key),
    )

// when we first load the Dashboard,
// we select the layout that is returned from the database by the this.loadDashboard this.props.loadDashboard call in
// app/static/frontend/dashboards/components/Dashboard.jsx
//
// immediately after that value is saved to the store, we select it from here
//
// after we select it, we want to insert the corresponding DashboardWidgetType min{W/H}/max{W/H} values into the layout
// to ensure that these values appear in the prop for the lifetime of the ReactGridLayout
//
// whenever we make changes to the Dashboard layout (either by adding/removing/resizing/moving a Widget),
// the ReactGridLayout automatically invokes its onLayoutChange listener and passes that updated layout (newLayout) as a parameter
//
// we then save that newLayout to the newLayout field in the Dashboard store that selectDashboardsValue selector selects from
// (this ensures that we do not re-render n number of Dashboard Widgets every single time we add/remove/resize/move a Widget)
//
// to ensure that the layout that we save to the Dashboard object is always up to date, whenever we segue or save,
// we invoke reconcileDashboardLayout to save the newLayout back into the layout field in the store
//
// this works because this selectLayout selector will only fire immediately after reconcileDashboardLayout,
// so these values should always be up to date
export const selectLayout = createSelector(
    selectDashboardsValue("layout"),
    selectDashboardsValue("widgets"),
    (layout, widgets) =>
        layout &&
        widgets &&
        layout.map((widgetPosition) => {
            let newWidgetPosition = widgetPosition
            const widgetTypeValue = widgets.getIn([newWidgetPosition.get("i"), "meta", "widget_type"])

            // we only update the layout when we call reconcileDashboardLayout(),
            // so if we have removed a widget with the getDasboardWidgetDefaultHeaderIcons widget-header-delete-icon,
            // we want to make sure that we don't perform widget-specific logic on the layout entry that will be removed
            // on the next reconcileDashboardLayout()
            if (widgetTypeValue) {
                // interpolate min and maxes based on the widget type for each position
                const widgetType = DjangIO.app.widgets.types.DashboardWidgetType.by_value(widgetTypeValue)
                const minMaxValues = ["minH", "maxH", "minW", "maxW"]

                minMaxValues.forEach((minMaxValue) => {
                    if (widgetType[minMaxValue]) {
                        newWidgetPosition = newWidgetPosition.set(minMaxValue, widgetType[minMaxValue])
                    }
                })
            }

            return newWidgetPosition
        }),
)

// selects a widget in a dashboard
// /dashboard/${dashboardId}/edit_widget/${widgetKey}/
export const selectWidgetById = createSelector(
    selectDashboardsValue("widgets"),
    selectDashboardsPropParam("widgetKey"),
    (store, widgetKey) => store && store.get(widgetKey),
)

// WidgetEditForm redux-form selectors
// app/static/frontend/dashboards/containers/WidgetEditForm

// selects the empty placeholder that we will display in the WidgetEditForm
// if we are not rendering a Widget component yet
export const selectEmptyPlaceholder = createSelector(selectDashboardsPropParam("widgetType"), (widgetType) => {
    switch (parseInt(widgetType)) {
        case DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value:
            return "Choose data on the right to fill the list widget"
        case DjangIO.app.widgets.types.DashboardWidgetType.text_widget.value:
            return "Add body text to fill the widget"
        case DjangIO.app.widgets.types.DashboardWidgetType.visualization_widget.value:
            return "Choose data on the right to fill the visualization widget"
        default:
            return "Choose data on the right to fill the widget"
    }
})

// select individual values from the WidgetEditForm redux form
export const selectWidgetEditFormValue = (key) =>
    createSelector(
        (state) => formValueSelector(WIDGET_EDIT_FORM_KEY)(state, key),
        (formValue) => formValue,
    )

// return all values from the WidgetEditForm redux form
export const selectWidgetEditFormValues = (state) => getFormValues(WIDGET_EDIT_FORM_KEY)(state)

// given the current selections in the WidgetEditForm redux-form,
// return the new widget so we can PATCH the dashboard
export const selectWidgetEditFormSearchifyInitialFilters = createSelector(
    selectWidgetEditFormValue("safedSearches"),
    selectWidgetEditFormValue("data_type"),
    selectDashboardsPropParam("widgetType"),
    (safedSearches, dataType, widgetType) =>
        safedSearches &&
        fromJS(
            safedSearches.map((safedSearch) => {
                const addedIds = safedSearch.get("added_ids") || []
                const deletedIds = safedSearch.get("deleted_ids") || []
                const filters = safedSearch.get("filters") || []
                // set default filter for Article FLV
                if (
                    !(addedIds.size + deletedIds.size + filters.size) &&
                    dataType === DjangIO.app.models.QuorumDataType.media.value &&
                    widgetType !== DjangIO.app.widgets.types.DashboardWidgetType.trending_widget.value
                ) {
                    return fromJS({
                        addedIds,
                        deletedIds,
                        querysetList: [
                            fromJS({
                                estimated_published_date_range: DjangIO.app.events.models.DateRangeType.today.value,
                                language: DjangIO.app.document.types.Language.english.value,
                            }),
                        ],
                    })
                }
                return fromJS({
                    // the widgetEngine saves keys with null values,
                    // so a .get with a fallback will still return null in some cases
                    // (this is why we '|| []' as well)
                    addedIds: addedIds,
                    deletedIds: deletedIds,
                    querysetList: filters,
                })
            }),
        ),
)

// matches Colors schema in app/widgets/types.py
export const getColors = ({ colors, colorToggleType }) =>
    // convert
    // Immutable.List[
    //     {
    //         colorKey: "0",
    //         colorValue: "#1a1a1a",
    //     },
    //     {
    //         colorKey: "22",
    //         colorValue: "2a2a2a"
    //    }
    // ]
    // to
    // {
    //     colors: Immutable.Map {
    //         0: "#1a1a1a",
    //         22: "2a2a2a",
    //     }
    // }
    colors && colors.size
        ? {
              colors: colors.reduce((acc, color, index) => {
                  const colorKey =
                      color.get("colorKey") ||
                      (colorToggleType
                          ? // if no boundary is defined, that means multi is false
                            // and we are only setting a single, globally bounded color
                            // (all possible values are >= 0)
                            "0"
                          : // for Visualization Widgets, the colorKey is always the index
                            index.toString())

                  return acc.set(colorKey && colorKey.replace(",", ""), color.get("colorValue"))
              }, new ImmutableMap()),
          }
        : {
              colors: fromJS({
                  0: QUORUM_BLUE,
              }),
          }

export const selectIsHomepageDashboard = createSelector(
    selectHomepageDashboardId,
    selectDashboardsPropParam("dashboardId"),
    (homepageDashboardId, dashboardId) => parseInt(homepageDashboardId) === parseInt(dashboardId),
)

/**
 * Creates the portion of the shape dealing with the interval period
 * for widget trends, default is the "day" interval
 *
 * @returns Object<TrendsGroupBy> - see app.widgets.TrendsGroupBy
 */
export const selectTrendsGroupByShape = createSelector(selectWidgetEditFormValue("interval"), (interval) => ({
    trending: {
        interval: interval || DjangIO.app.hubble.queryset.TrendingPeriodEnum.day.value,
    },
}))

/**
 * For selectTrendsWidget - Returns an object consisting of:
 *      (1) the safed_search object
 *      (2) the group by interval (made from createTrendsGroupByShape)
 *
 * @param Object<SafedSearch>
 * @returns Object<TrendingDataSource> [see app.widgets.TrendingDataSource]
 *
 */
export const selectTrendingDataSourceShape = createSelector(
    selectWidgetEditFormValue("safedSearches"),
    selectTrendsGroupByShape,
    (safedSearches, trendingDataShape) =>
        safedSearches && safedSearches.size
            ? safedSearches.map((safedSearch) =>
                  fromJS({
                      group_by: trendingDataShape,
                      safed_search: {
                          added_ids: safedSearch.get("added_ids", []) || [],
                          data_type: DjangIO.app.models.QuorumDataType.media.value,
                          deleted_ids: safedSearch.get("deleted_ids", []) || [],
                          filters: safedSearch.get("filters", []) || [],
                      },
                  }),
              )
            : fromJS([
                  {
                      group_by: trendingDataShape,
                      safed_search: { data_type: DjangIO.app.models.QuorumDataType.media.value },
                  },
              ]),
)

// given the current selections in the WidgetEditForm redux-form,
// return the new widget schema so we can PATCH or POST it to the dashboard widget endpoint
export const selectWidgetEditFormWidget = createSelector(
    selectDashboardsPropParam("widgetKey"),
    selectDashboardsPropParam("widgetType"),
    selectWidgetEditFormValue("data_type"),
    selectWidgetEditFormValue("safedSearches"),
    selectWidgetEditFormValue("description"),
    selectWidgetEditFormValue("text"),
    selectWidgetEditFormValue("text_toggle_type"),
    selectWidgetEditFormValue("quicklinkType"),
    selectWidgetEditFormValue("quicklinkUrl"),
    selectWidgetEditFormValue("displayType"),
    selectWidgetEditFormValue("visSubtype"),
    selectWidgetEditFormValue("interval"),
    selectWidgetEditFormValue("labels"),
    selectWidgetEditFormValue("visCumulative"),
    selectWidgetEditFormValue("colors"),
    selectWidgetEditFormValue("color_toggle_type"),
    selectWidgetEditFormValue("bar_visualization_display_style"),
    selectWidgetEditFormValue("frequency_tuple_field"),
    selectWidgetEditFormValue("date_field"),
    selectWidgetEditFormValue("count"),
    selectWidgetEditFormValue("bar_visualization_direction"),
    selectWidgetEditFormValue("map_visualization_custom_colors"),
    selectWidgetEditFormValue("map_visualization_custom_colors_key"),
    selectWidgetEditFormValue("map_visualization_heat_color_scale"),
    selectWidgetEditFormValue("map_visualization_heat_color_type"),
    selectWidgetEditFormValue("map_visualization_heat_interpolation_color_space"),
    selectWidgetEditFormValue("map_visualization_heat_interpolation_colors"),
    selectWidgetEditFormValue("map_visualization_heat_quantized_color_step"),
    selectWidgetEditFormValue("map_visualization_region"),
    selectTrendingDataSourceShape,
    selectWidgetEditFormValue("aggregation_field"),
    (
        widgetKey,
        widgetType,
        dataType,
        safedSearches,
        description,
        text,
        textToggleType,
        quicklinkType,
        quicklinkUrl,
        displayType,
        visSubtype,
        interval,
        labels,
        visCumulative,
        colors,
        colorToggleType,
        barVisualizationDisplayStyle,
        frequencyTupleField,
        dateField,
        count,
        barVisualizationDirection,
        mapVisualizationCustomColors,
        mapVisualizationCustomColorsKey,
        mapVisualizationHeatColorScale,
        mapVisualizationHeatColorType,
        mapVisualizationHeatInterpolationColorSpace,
        mapVisualizationHeatInterpolationColors,
        mapVisualizationHeatQuantizedColorStep,
        mapVisualizationRegion,
        trendingDataSourceShape,
        aggregationField,
    ) => {
        // matches BaseDashboardWidget schema in app/widgets/types.py
        const baseDashboardWidget = {
            meta: {
                // description?:str
                ...(description || description === ""
                    ? {
                          description:
                              // we handle this automatically in:
                              //      - {Bar/Line/Pie}FromSection.jsx,
                              //      - ListWidgetSidebarForm.jsx
                              //      - TextWidgetSidebarForm.jsx,
                              // through maxTextLength={dashboardConstants.maxTextLength},
                              // but we should also enforce this globally in the selector just in case
                              parseInt(widgetType) === DjangIO.app.widgets.types.DashboardWidgetType.text_widget.value
                                  ? description
                                  : description.substring(0, dashboardConstants.maxTextLength),
                      }
                    : {}),
                // text?:str
                ...(textToggleType
                    ? // we use " " because we still want to display
                      // the gray header bar if the user has chosen to "Show Title"
                      { text: text || " " }
                    : {}),
                // id?:str
                ...(widgetKey ? { id: widgetKey } : {}),
                // labels?:[str]
                ...(labels ? { labels } : {}),
                // widget_type:widget_type
                widget_type: parseInt(widgetType),
            },
        }

        // matches DataSource schema in app/widgets/types.py
        const dataSources = safedSearches
            ? safedSearches.map((safedSearch) =>
                  fromJS({
                      // safed_search:safed_search
                      safed_search: safedSearch,
                  }),
              )
            : []

        // matches CountDataSource schema in app/widgets/types.py
        // most widgets have dataSource(s), but each respective widget
        // may need to apply transformations so that it matches the schema
        const getCountDataSources = (baseDataSources) =>
            baseDataSources.map((dataSource) =>
                // count:has_count
                dataSource.set("count", true),
            )

        // matches GroupByDataSource schema in app/widgets/types.py
        const getGroupByDataSources = ({ baseDataSources, groupBy }) =>
            baseDataSources.map((dataSource) =>
                // VisualizationSubtype.map
                // frequency_tuple_field == FrequencyTupleFields.custom
                !dataSource.getIn(["safed_search", "data_type"])
                    ? dataSource.set("safed_search", null).set("group_by", groupBy)
                    : dataSource.set("group_by", groupBy),
            )

        const selectListWidget = () =>
            fromJS({
                ...baseDashboardWidget,
                data_sources: dataSources,
            })

        /**
         * Returns the trending widget shape
         *
         * @returns Object<DashboardTrendingWidget> [see app.widgets.types.DashboardTrendingWidget]
         */
        const selectTrendsWidget = () =>
            fromJS({
                ...baseDashboardWidget,
                data_sources: trendingDataSourceShape,
            })

        const selectNumberWidget = () =>
            fromJS({
                ...baseDashboardWidget,
                // colors?:colors
                ...getColors({
                    colors,
                    colorToggleType,
                }),
                data_sources:
                    displayType === DjangIO.app.widgets.types.NumberWidgetDisplayType.sum.value && aggregationField
                        ? // matches GroupByDataSource schema in app/widgets/types.py
                          getGroupByDataSources({
                              baseDataSources: dataSources,
                              groupBy: ImmutableMap({
                                  aggregation_field:
                                      DjangIO.app.widgets.types.AggregationFields.by_value(aggregationField).value,
                              }),
                          })
                        : // matches CountDataSource schema in app/widgets/types.py
                          getCountDataSources(dataSources),
                ...(displayType &&
                    DjangIO.app.widgets.types.NumberWidgetDisplayType.by_value(displayType).operation && {
                        operation: DjangIO.app.widgets.types.NumberWidgetDisplayType.by_value(displayType).operation,
                    }),
            })

        const selectQuicklinkWidget = () => {
            const widget = { ...baseDashboardWidget }
            widget.meta.quicklink = {
                text,
                url: quicklinkUrl,
                type: quicklinkType,
            }
            return fromJS(widget)
        }

        const selectTextWidget = () => fromJS({ ...baseDashboardWidget })

        const selectVisualizationWidget = () => {
            const isBarLineVisualizationField =
                visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value &&
                frequencyTupleField &&
                DjangIO.app.widgets.types.FrequencyTupleFields.date_fields.includes(frequencyTupleField)

            // matches DashboardGroupByWidget in app/widgets/types.py
            if (
                visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value ||
                visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value
            ) {
                return fromJS({
                    ...baseDashboardWidget,
                    data_sources:
                        (visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value && !count) ||
                        visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value
                            ? getGroupByDataSources({
                                  baseDataSources: dataSources,
                                  groupBy: ImmutableMap({
                                      ...// Bar Visualizations will always contain a FrequencyTupleFields field
                                      (visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value && {
                                          frequency_tuple_field:
                                              DjangIO.app.widgets.types.FrequencyTupleFields.by_value(
                                                  frequencyTupleField,
                                              ).value,
                                      }),
                                      ...// Line Visualizations will always contain a DateFields field
                                      // Bar Visualizations can contain a DateFields field if the
                                      // FrequencyTupleFields field is contained within FrequencyTupleFields.date_fields
                                      ((visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value ||
                                          isBarLineVisualizationField) && {
                                          date_field: DjangIO.app.widgets.types.DateFields.by_value(dateField).value,
                                      }),
                                      // Line Visualizations will always contain an interval (at the moment)
                                      ...(visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value && {
                                          interval,
                                      }),
                                      ...// Like Line Visualizations, Bar Visualizations can contain an interval
                                      // if the FrequencyTupleFields field is contained within FrequencyTupleFields.date_fields
                                      (isBarLineVisualizationField && {
                                          interval:
                                              frequencyTupleField &&
                                              // if we are rendering a Bar widget with a Line group_by,
                                              // we need to find the key of the enum item in LineGraphTimeIntervals
                                              // which matches the key of the currently selected FrequencyTupleFields enum item
                                              DjangIO.app.widgets.types.LineGraphTimeIntervals.items().find(
                                                  (lineGraphTimeInterval) =>
                                                      lineGraphTimeInterval.key ===
                                                      DjangIO.app.widgets.types.FrequencyTupleFields.by_value(
                                                          frequencyTupleField,
                                                      ).key,
                                              ).value,
                                      }),
                                  }),
                              })
                            : // matches CountDataSource schema in app/widgets/types.py
                              getCountDataSources(dataSources),
                    meta: {
                        ...baseDashboardWidget.meta,
                        // colors?:colors
                        ...getColors({ colors }),
                        vis_subtype: visSubtype,
                        vis_options: {
                            ...(visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value && {
                                bar_visualization_direction: barVisualizationDirection,
                            }),
                            ...(visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value && {
                                bar_visualization_display_style: barVisualizationDisplayStyle,
                            }),
                            ...((visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value ||
                                isBarLineVisualizationField) && { cumulative: visCumulative }),
                        },
                    },
                })
                // matches DashboardPieVisualizationWidget schema in app/widgets/types.py
            } else if (visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.pie.value) {
                return fromJS({
                    ...baseDashboardWidget,
                    data_sources:
                        displayType === DjangIO.app.widgets.types.NumberWidgetDisplayType.sum.value && aggregationField
                            ? // matches GroupByDataSource schema in app/widgets/types.py
                              getGroupByDataSources({
                                  baseDataSources: dataSources,
                                  groupBy: ImmutableMap({
                                      aggregation_field:
                                          DjangIO.app.widgets.types.AggregationFields.by_value(aggregationField).value,
                                  }),
                              })
                            : // matches CountDataSource schema in app/widgets/types.py
                              getCountDataSources(dataSources),
                    // matches DashboardWidgetMeta schema in app/widgets/types.py
                    meta: {
                        ...baseDashboardWidget.meta,
                        // colors?:colors
                        ...getColors({ colors }),
                        vis_options: {},
                        vis_subtype: visSubtype,
                    },
                })
                // matches DashboardMapVisualizationWidget schema in app/widgets/types.py
            } else if (visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.map.value) {
                return fromJS({
                    ...baseDashboardWidget,
                    // matches DataSource schema in app/widgets/types.py
                    data_sources:
                        // matches GroupByDataSource schema in app/widgets/types.py
                        getGroupByDataSources({
                            baseDataSources: dataSources,
                            groupBy: ImmutableMap({
                                frequency_tuple_field:
                                    DjangIO.app.widgets.types.FrequencyTupleFields.by_value(frequencyTupleField).value,
                            }),
                        }),
                    // matches DashboardWidgetMeta schema in app/widgets/types.py
                    meta: {
                        ...baseDashboardWidget.meta,
                        // colors?:colors
                        ...getColors({ colors }),
                        vis_options: {
                            map_visualization_custom_colors: mapVisualizationCustomColors,
                            map_visualization_custom_colors_key: mapVisualizationCustomColorsKey,
                            map_visualization_heat_color_scale: mapVisualizationHeatColorScale,
                            map_visualization_heat_color_type: mapVisualizationHeatColorType,
                            map_visualization_heat_interpolation_color_space:
                                mapVisualizationHeatInterpolationColorSpace,
                            map_visualization_heat_interpolation_colors: mapVisualizationHeatInterpolationColors,
                            map_visualization_heat_quantized_color_step: mapVisualizationHeatQuantizedColorStep,
                            map_visualization_region: mapVisualizationRegion,
                        },
                        vis_subtype: visSubtype,
                    },
                })
            }

            return undefined
        }

        switch (parseInt(widgetType)) {
            // matches DashboardListWidget in app/widgets/types.py
            case DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.calendar_widget.value:
                return selectListWidget()
            // matches DashboardNumberWidget in app/widgets/types.py
            case DjangIO.app.widgets.types.DashboardWidgetType.number_widget.value:
                return selectNumberWidget()
            // matches Quicklink in app/widgets/types.py
            case DjangIO.app.widgets.types.DashboardWidgetType.quicklink_widget.value:
                return selectQuicklinkWidget()
            // matches DashboardWidgetMeta in app/widgets/types.py (text?:str)
            case DjangIO.app.widgets.types.DashboardWidgetType.text_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.favorites_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.recents_widget.value:
                return selectTextWidget()
            case DjangIO.app.widgets.types.DashboardWidgetType.visualization_widget.value:
                return selectVisualizationWidget()
            case DjangIO.app.widgets.types.DashboardWidgetType.trending_widget.value:
                return selectTrendsWidget()
            default:
                return undefined
        }
    },
)

/**
 * @function
 * Gets the default header icons for all widgets in a dashboard
 * app/static/frontend/dashboards/components/Dashboard
 * @param {Function} createOrUpdateDashboard - action creator that creates or updates a Dashboard in the db
 * @param {Array[String]} deletedWidgets - array of Widget strings that have been deleted in the Dashboard (["ABCD", "CEBX", ...])
 * @param {bool} editing - whether or not we are currently editing the Dashboard
 * @param {int} id - the id of the Dashboard
 * @param {bool} isLoadingDownloadWidget - whether or not the current widget widgetKey is being downloaded
 * @param {int} name - the name of the Dashboard
 * @param {Function} removeFromDashboardLayout - removes key/value from layout/newLayout in the Dashboard store
 * @param {Function} sendAction - action creator for handling any request an action can handle (GET/POST/PATCH/DELETE)
 * @param {Function} segueToUrl - global action creator that will segue to a specific URI in Quorum
 * @param {Function} setDashboardsValues - updates the given key value pairs in values for dashboard id in the store
 * @param {Function} submitWidgetTemplateSelection - creates a new widget using this widget as a template
 * @param {object} widgets - an object containing all widgets for the Dashboard
 * @param {string} widgetKey - the key of the widget in the widgets Object
 *
 * @returns {Array<Object>} Array of Widget header icons
 */
export const getDasboardWidgetDefaultHeaderIcons = ({
    createOrUpdateDashboard,
    deletedWidgets,
    editing,
    id,
    isLoadingDownloadWidget,
    name,
    removeFromDashboardLayout,
    segueToUrl,
    sendAction,
    setDashboardsValues,
    submitWidgetTemplateSelection,
    widgetKey,
    widgets,
    externalDownloadsDisabled,
    isExternal,
}) =>
    editing
        ? [
              {
                  dataCy: "widget-header-duplicate-icon",
                  onClick: () => {
                      // use this widget as a template to create a new one
                      submitWidgetTemplateSelection({
                          dashboardId: id,
                          widget: widgets[widgetKey],
                      })
                  },
                  icon: "copy",
                  iconFamily: "fas",
                  size: "lg",
              },
              {
                  dataCy: "widget-header-edit-icon",
                  onClick: () => {
                      saveDashboard({
                          createOrUpdateDashboard,
                          deletedWidgets,
                          id,
                          name,
                          sendAction,
                          setDashboardsValues,
                      })

                      segueToUrl(
                          dashboardPaths.editWidget
                              .replace(":dashboardId", id)
                              .replace(":widgetType", widgets[widgetKey].meta.widget_type)
                              .replace(":widgetKey?", `${widgetKey}`),
                      )
                  },
                  icon: "pencil-alt",
                  iconFamily: "fas",
                  size: "lg",
              },
              {
                  dataCy: "widget-header-delete-icon",
                  icon: "trash-alt",
                  iconFamily: "fas",
                  onClick: () => {
                      onRemoveItemHandler({
                          id,
                          index: widgetKey,
                          removeFromDashboardLayout,
                      })
                  },
                  size: "lg",
              },
          ]
        : ![
                // csv, word, and excel download for list_widget and calendar_widget edge cases handled in
                // app/static/frontend/widgets/selectors/listWidgetSelectors.js getListWidgetHeaderIcons
                // through
                // app/widgets/api.py widget_engine
                DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value,
                DjangIO.app.widgets.types.DashboardWidgetType.calendar_widget.value,
                DjangIO.app.widgets.types.DashboardWidgetType.quicklink_widget.value,
            ].includes(widgets[widgetKey].meta.widget_type) &&
            (!isExternal || (isExternal && !externalDownloadsDisabled))
          ? [
                {
                    dataCy: "widget-header-download-icon",
                    icon: isLoadingDownloadWidget ? "spinner" : "download",
                    iconFamily: "fas",
                    id: "widget-header-download-icon",
                    onClick: async () => {
                        if (!isLoadingDownloadWidget) {
                            setDashboardsValues({
                                id,
                                values: {
                                    isLoadingDownloadWidgets: {
                                        [widgetKey]: true,
                                    },
                                },
                            })

                            // the computationally intensive saveElementAsImage blocks before React can rerender,
                            // so we want to rerender before we generate the download
                            await new Promise((r) => setTimeout(r, 10))

                            await saveElementAsImage({
                                element: document.getElementById(`widget-${widgetKey}`),
                                format: "png",
                                title: `dashboard-${id}-widget-${widgetKey}`,
                            })

                            setDashboardsValues({
                                id,
                                values: {
                                    isLoadingDownloadWidgets: undefined,
                                },
                            })
                        }
                    },
                    size: "lg",
                    spin: isLoadingDownloadWidget,
                },
            ]
          : []

export const widgetCreateFormConnection = createStructuredSelector({
    hasMoreTemplates: selectWidgetTemplatesHasMore,
    isLoadingTemplates: selectWidgetTemplatesLoading,
    widgetTemplates: selectWidgetTemplateObjsForCreateForm,
})
